import { User } from 'fitify-types/src/types/user'
import { isDefined } from 'fitify-utils/src/properties'

import { DEFAULT_PROMO_CODE } from 'data/promo'
import type { FunnelRoutesDefinition } from 'types/routes'
import { type DataPathState, Funnel, Question } from 'types/state'

import { getSubscriptionFlowPages } from '../../utils/funnel-pages'
import { getGymSectionDifficulty } from '../../utils/gym'

type GymM01DataState = DataPathState<Funnel.GYMM01>

const pages = {
  welcomeAgeBuckets: {
    path: '/gymm01/welcome-age-buckets',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    isExcludedFromProgressBarOrStepper: true,
    stateKey: 'age_bucket',
    id: 'welcome_age_buckets',
  },
  socialProofMillions: {
    path: '/gymm01/social-proof-millions',
    isValidatedStep: true,
    id: 'social_proof_millions',
  },
  experience: {
    path: '/gymm01/experience',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'experience',
    id: 'experience',
  },
  experiencePitch: {
    path: '/gymm01/experience-pitch',
    isValidatedStep: true,
    id: 'experience_pitch',
    condition: (data: GymM01DataState) => {
      return isDefined(data.experience)
    },
  },
  mainGoal: {
    path: '/gymm01/main-goal',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'goal',
    id: 'main_goal',
    required: true,
  },
  goalPitch: {
    path: '/gymm01/goal-pitch',
    isValidatedStep: true,
    id: 'goal_pitch',
    condition: (data: GymM01DataState) => {
      return isDefined(data.goal)
    },
  },
  motivationEarly: {
    path: '/gymm01/motivation-early',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'motivation_early',
    id: 'motivation_early',
  },
  didYouKnowHormones: {
    path: '/gymm01/did-you-know-hormones',
    isValidatedStep: true,
    id: 'did_you_know_hormones',
  },
  didYouKnowDepression: {
    path: '/gymm01/did-you-know-depression',
    isValidatedStep: true,
    id: 'did_you_know_depression',
  },
  didYouKnowLongevity: {
    path: '/gymm01/did-you-know-longevity',
    isValidatedStep: true,
    id: 'did_you_know_longevity',
  },
  physicalBuild: {
    path: '/gymm01/physical-build',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'current_shape',
    id: 'physical_build',
  },
  bodyGoal: {
    path: '/gymm01/body-goal',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'body_goal',
    id: 'body_goal',
  },
  weightChangeExp: {
    path: '/gymm01/weight-change-exp',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'weight_change_exp',
    id: 'weight_change_exp',
  },
  bodyTypePitch: {
    path: '/gymm01/body-type-pitch',
    isValidatedStep: true,
    id: 'body_type_pitch',
    condition: (data: GymM01DataState) => {
      return isDefined(data.weight_change_exp)
    },
  },
  bestShape: {
    path: '/gymm01/best-shape',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'latest_ideal_weight',
    id: 'best_shape',
  },
  consistencyPitch: {
    path: '/gymm01/consistency-pitch',
    isValidatedStep: true,
    id: 'consistency_pitch',
  },
  problemAreas: {
    path: '/gymm01/problem-areas',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'problem_areas',
    id: 'problem_areas',
    required: true,
  },
  problemAreasPitch: {
    path: '/gymm01/problem-areas-pitch',
    isValidatedStep: true,
    id: 'problem_areas_pitch',
  },
  struggle: {
    path: '/gymm01/struggle',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'struggle',
    id: 'struggle',
  },
  strugglePitch: {
    path: '/gymm01/struggle-pitch',
    isValidatedStep: true,
    id: 'struggle_pitch',
    condition: (data: GymM01DataState) => {
      return Boolean(
        data.struggle?.length &&
        data.struggle.some((item) => item !== Question.Struggle.None)
      )
    },
  },
  workoutLocation: {
    path: '/gymm01/workout-location',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'workout_location',
    id: 'workout_location',
  },
  gymType: {
    path: '/gymm01/gym-type',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'gym_type',
    id: 'gym_type',
    condition: (data: GymM01DataState) => {
      return (
        data.workout_location === Question.WorkoutLocation.Gym ||
        data.workout_location === Question.WorkoutLocation.Both ||
        data.workout_location === Question.WorkoutLocation.DoNotKnow
      )
    },
  },
  equipmentPreference: {
    path: '/gymm01/equipment-preference',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'equipment_preference',
    id: 'equipment_preference',
    condition: (data: GymM01DataState) => {
      return data.workout_location === Question.WorkoutLocation.Gym
    },
  },
  gymConfidence: {
    path: '/gymm01/gym-confidence',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'gym_confidence',
    id: 'gym_confidence',
    condition: (data: GymM01DataState) => {
      return data.workout_location === Question.WorkoutLocation.Gym
    },
  },
  gymConfidencePitch: {
    path: '/gymm01/gym-confidence-pitch',
    isValidatedStep: true,
    id: 'gym_confidence_pitch',
    condition: (data: GymM01DataState) => {
      return (
        data.workout_location === Question.WorkoutLocation.Gym &&
        isDefined(data.gym_confidence) &&
        data.gym_confidence !== Question.GymConfidence.Confident
      )
    },
  },
  homeGym: {
    path: '/gymm01/home-gym',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'home_gym',
    id: 'home_gym',
    condition: (data: GymM01DataState) => {
      return (
        data.workout_location === Question.WorkoutLocation.HomeGym ||
        data.workout_location === Question.WorkoutLocation.Both ||
        data.workout_location === Question.WorkoutLocation.DoNotKnow
      )
    },
  },
  locationCombinationPitch: {
    path: '/gymm01/location-combination-pitch',
    isValidatedStep: true,
    id: 'location_combination_pitch',
    condition: (data: GymM01DataState) => {
      return data.workout_location === Question.WorkoutLocation.Both
    },
  },
  gymDifficulty: {
    path: '/gymm01/gym-difficulty',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'gym_difficulty',
    id: 'gym_difficulty',
    condition: (data: GymM01DataState) => {
      return (
        data.workout_location === Question.WorkoutLocation.Gym ||
        data.workout_location === Question.WorkoutLocation.Both
      )
    },
  },
  homeDifficulty: {
    path: '/gymm01/home-difficulty',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'home_difficulty',
    id: 'home_difficulty',
    condition: (data: GymM01DataState) => {
      return (
        data.workout_location === Question.WorkoutLocation.HomeGym ||
        data.workout_location === Question.WorkoutLocation.DoNotKnow
      )
    },
  },
  difficultyPitch: {
    path: '/gymm01/difficulty-pitch',
    isValidatedStep: true,
    id: 'difficulty_pitch',
    condition: (data: GymM01DataState) => {
      const difficulty = getGymSectionDifficulty(data)
      return (
        isDefined(difficulty) &&
        difficulty !== Question.GymDifficulty.None &&
        difficulty !== Question.HomeDifficulty.None
      )
    },
  },
  howOftenExercise: {
    path: '/gymm01/how-often-exercise',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'how_often_exercise',
    id: 'how_often_exercise',
  },
  workoutFrequency: {
    path: '/gymm01/workout-frequency',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'workout_frequency',
    id: 'workout_frequency',
  },
  workoutLength: {
    path: '/gymm01/workout-length',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'workout_length',
    id: 'workout_length',
  },
  bigResultsPitch: {
    path: '/gymm01/big-results-pitch',
    isValidatedStep: true,
    id: 'big_results_pitch',
  },
  howOftenWalks: {
    path: '/gymm01/how-often-walks',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'how_often_walks',
    id: 'how_often_walks',
  },
  fitnessLevelSquats: {
    path: '/gymm01/fitness-level-squats',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'fitness_level_squats',
    id: 'fitness_level_squats',
  },
  fitnessLevelPushups: {
    path: '/gymm01/fitness-level-pushups',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'fitness_level_push_ups',
    id: 'fitness_level_pushups',
  },
  fitnessLevelPlank: {
    path: '/gymm01/fitness-level-plank',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'fitness_level_plank',
    id: 'fitness_level_plank',
  },
  flexible: {
    path: '/gymm01/flexible',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'flexible',
    id: 'flexible',
  },
  workSchedule: {
    path: '/gymm01/work-schedule',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'work_schedule',
    id: 'work_schedule',
  },
  stress: {
    path: '/gymm01/stress',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'stress',
    id: 'stress',
  },
  cortisolPitch: {
    path: '/gymm01/cortisol-pitch',
    isValidatedStep: true,
    id: 'cortisol_pitch',
  },
  typicalDayActivity: {
    path: '/gymm01/typical-day-activity',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'typical_day_activity',
    id: 'typical_day_activity',
  },
  energyLevels: {
    path: '/gymm01/energy-levels',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'energy_levels',
    id: 'energy_levels',
  },
  energyPitch: {
    path: '/gymm01/energy-pitch',
    isValidatedStep: true,
    id: 'energy_pitch',
  },
  waterIntake: {
    path: '/gymm01/water-intake',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'water_intake',
    id: 'water_intake',
  },
  sleepDuration: {
    path: '/gymm01/sleep-duration',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'sleep_duration',
    id: 'sleep_duration',
  },
  diet: {
    path: '/gymm01/diet',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'diet',
    id: 'diet',
  },
  habits: {
    path: '/gymm01/habits',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'habits',
    id: 'habits',
  },
  cravings: {
    path: '/gymm01/cravings',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'cravings',
    id: 'cravings',
  },
  smoking: {
    path: '/gymm01/smoking',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'smoking',
    id: 'smoking',
  },
  alcohol: {
    path: '/gymm01/alcohol',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'alcohol',
    id: 'alcohol',
  },
  lifeEventsStopped: {
    path: '/gymm01/life-events-stopped',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'life_events_stopped',
    id: 'life_events_stopped',
  },
  eligibilityPitch: {
    path: '/gymm01/eligibility-pitch',
    isValidatedStep: true,
    id: 'eligibility_pitch',
  },
  height: {
    path: '/gymm01/height',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'height',
    id: 'height',
    required: true,
  },
  weight: {
    path: '/gymm01/weight',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'weight',
    id: 'weight',
    required: true,
  },
  goalWeight: {
    path: '/gymm01/goal-weight',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'goal_weight',
    id: 'goal_weight',
    required: true,
  },
  age: {
    path: '/gymm01/age',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'age',
    id: 'age',
    required: true,
  },
  wellnessProfile: {
    path: '/gymm01/wellness-profile',
    isValidatedStep: true,
    id: 'wellness_profile',
    condition: (data: GymM01DataState) => {
      return (
        isDefined(data.typical_day_activity) &&
        isDefined(data.current_shape) &&
        isDefined(data.how_often_exercise) &&
        isDefined(data.weight_change_exp)
      )
    },
  },
  lifeEvent: {
    path: '/gymm01/life-event',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'life_event',
    id: 'life_event',
  },
  lifeEventDate: {
    path: '/gymm01/life-event-date',
    isValidatedStep: true,
    stateKey: 'life_event_date',
    id: 'life_event_date',
    condition: (data: GymM01DataState) => {
      return (
        isDefined(data.life_event) &&
        data.life_event !== User.Profile.LifeEvent.NoEvent
      )
    },
  },
  lifeEventGraph: {
    path: '/gymm01/life-event-graph',
    isValidatedStep: true,
    id: 'life_event_graph',
    condition: (data: GymM01DataState) => {
      return isDefined(data.goal_weight) && isDefined(data.weight)
    },
  },
  credibilityPitch: {
    path: '/gymm01/credibility-pitch',
    isValidatedStep: true,
    id: 'credibility_pitch',
  },
  planGenerator: {
    path: '/gymm01/plan-generator',
    id: 'plan_generator',
  },
  email: {
    path: '/gymm01/email',
    id: 'email',
    stateKey: 'email',
  },
  name: {
    path: '/gymm01/name',
    isValidatedStep: true,
    stateKey: 'displayName',
    id: 'name',
  },
  namePitch: {
    path: '/gymm01/name-pitch',
    isValidatedStep: true,
    id: 'name_pitch',
  },
  discountReveal: {
    path: '/gymm01/discount-reveal',
    isValidatedStep: true,
    id: 'discount_reveal',
    condition: (_, { promoCode }) => {
      return Boolean(!promoCode || promoCode === DEFAULT_PROMO_CODE)
    },
  },
  ...getSubscriptionFlowPages(Funnel.GYMM01),
} as const satisfies FunnelRoutesDefinition<GymM01DataState>['pages']

const pagesMap = Object.values(pages)

const detectLocationPagesMap = [
  pages.planGenerator,
  pages.subscription,
  pages.exclusiveOffer,
] as const satisfies FunnelRoutesDefinition<GymM01DataState>['detectLocationPagesMap']

const noLeaveDialogPagesMap = [
  pages.subscription,
  pages.exclusiveOffer,
  pages.success,
] as const satisfies FunnelRoutesDefinition<GymM01DataState>['noLeaveDialogPagesMap']

export const GYMM01_ROUTES = {
  detectLocationPagesMap,
  noLeaveDialogPagesMap,
  pages,
  pagesMap,
} as const satisfies FunnelRoutesDefinition<GymM01DataState>
